import React, { useState } from 'react';
import { Modal, Button, Radio, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont';
import { useTranslation } from 'react-i18next';

const ScheduleDownloadModal = ({ 
  isVisible, 
  onClose, 
  upcomingSchedule = [], 
  pastSchedule = [],
  student = null
}) => {
  const { t } = useTranslation();
  const [scheduleType, setScheduleType] = useState('upcoming');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date';
  
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const calculateEndTime = (startTime, duration) => {
    if (!startTime || !duration) return '';
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [durationHours, durationMinutes] = duration.split(':').map(Number);
    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const loadSarabunFont = (doc) => {
    doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
    doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
    doc.setFont('Sarabun');
  };

  const handleDownloadPDF = () => {
    if (!student) return;

    // Determine which data to use
    let scheduleData = [];
    let filenameSuffix = '';
    
    switch(scheduleType) {
      case 'upcoming':
        scheduleData = upcomingSchedule;
        filenameSuffix = 'upcoming';
        break;
      case 'past':
        scheduleData = pastSchedule;
        filenameSuffix = 'past';
        break;
      case 'all':
        scheduleData = [...upcomingSchedule, ...pastSchedule].sort((a, b) => {
          const dateTimeA = new Date(`${a.date}T${a.time || '00:00'}`);
          const dateTimeB = new Date(`${b.date}T${b.time || '00:00'}`);
          return dateTimeA - dateTimeB;
        });
        filenameSuffix = 'all';
        break;
      default:
        scheduleData = upcomingSchedule;
        filenameSuffix = 'upcoming';
    }

    if (scheduleData.length === 0) {
      Modal.warning({
        title: t('No Data'),
        content: t('No schedule data available for the selected period.')
      });
      return;
    }

    const doc = new jsPDF();
    loadSarabunFont(doc);

    const margin = 20;
    const keyColor = '#95101e';
    const pageWidth = doc.internal.pageSize.getWidth();

    const headerImage = new Image();
    headerImage.src = '/images/advisorlogo.png';

    headerImage.onload = () => {
      // Header box dimensions
      const headerBoxHeight = 35;
      const headerBoxY = margin;
      
      // Draw opaque background box for header
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, headerBoxY, pageWidth - (margin * 2), headerBoxHeight, 'F');
      
      // Add subtle border to the header box
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.5);
      doc.rect(margin, headerBoxY, pageWidth - (margin * 2), headerBoxHeight, 'S');

      // Calculate logo dimensions and position
      const imgAspectRatio = headerImage.width / headerImage.height;
      const logoHeight = 25;
      const logoWidth = logoHeight * imgAspectRatio;
      const logoX = margin + 10;
      const logoY = headerBoxY + 5;

      // Add logo
      doc.addImage(headerImage, 'PNG', logoX, logoY, logoWidth, logoHeight);

      // Contact information on the right side
      const contactX = pageWidth - margin - 10;
      const contactStartY = headerBoxY + 8;
      
      // Company/Institute name
      doc.setFontSize(12);
      doc.setTextColor(keyColor);
      doc.setFont('helvetica', 'bold');
      doc.text('The Advisor Academy', contactX, contactStartY, { align: 'right' });
      
      // Address
      doc.setFontSize(9);
      doc.setTextColor(51, 51, 51);
      doc.setFont('helvetica', 'normal');
      doc.text('1402 Siamscape 14th floor, Pathum Wan, ', contactX, contactStartY + 6, { align: 'right' });
      doc.text('Bangkok 10330, Thailand', contactX, contactStartY + 12, { align: 'right' });
      
      // Contact information
      doc.setFontSize(9);
      doc.setTextColor(keyColor);
      doc.text('Tel: +66 083 628 9191', contactX, contactStartY + 18, { align: 'right' });

      // Student name below header box
      const centerX = pageWidth / 2;
      
      doc.setFontSize(18);
      doc.setTextColor(keyColor);
      doc.setFont('helvetica', 'bold');
      doc.text(`${student.firstname} ${student.lastname}`, centerX, headerBoxY + headerBoxHeight + 15, { align: 'center' });

      // Schedule type indicator
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(51, 51, 51);
      let scheduleTypeText = '';
      switch(scheduleType) {
        case 'upcoming':
          scheduleTypeText = t('Upcoming Classes');
          break;
        case 'past':
          scheduleTypeText = t('Past Classes');
          break;
        case 'all':
          scheduleTypeText = t('All Classes');
          break;
        default:
          scheduleTypeText = '';
      }
      doc.text(scheduleTypeText, centerX, headerBoxY + headerBoxHeight + 25, { align: 'center' });

      // Table starts after the header info
      const tableStartY = headerBoxY + headerBoxHeight + 35;

      const columns = [
        { header: t('date'), dataKey: 'date' },
        { header: t('day'), dataKey: 'day' },
        { header: t('Course Code'), dataKey: 'class_code' },
        { header: t('time'), dataKey: 'time' },
        { header: t('hours'), dataKey: 'hours' },
        { header: t('subjectName'), dataKey: 'subject' },
        { header: t('teacherName'), dataKey: 'teacher' },

      ];

      const tableData = scheduleData.map(row => ({
        date: formatDate(row.date),
        day: new Date(row.date).toLocaleDateString('en-US', { weekday: 'short' }),
        class_code: row.class_code || '',
        time: `${formatTime(row.time)} - ${calculateEndTime(row.time, row.hours)}`,
        hours: row.hours || '',
        subject: row.subject || '',
        teacher: row.teacher || '',

      }));

      doc.autoTable({
        startY: tableStartY,
        head: [columns.map(col => col.header)],
        body: tableData.map(row => columns.map(col => row[col.dataKey])),
        theme: 'grid',
        styles: {
          font: 'Sarabun',
          fontStyle: 'normal',
          fontSize: 10,
          textColor: [51, 51, 51],
          lineColor: [200, 200, 200],
          cellPadding: 2,
          overflow: 'linebreak',
          halign: 'left'
        },
        headStyles: {
          fillColor: [149, 16, 30],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10
        },
        alternateRowStyles: {
          fillColor: [252, 252, 252]
        },
        columnStyles: {
          0: { cellWidth: 30 },  // date
          1: { cellWidth: 15 },  // day
          2: { cellWidth: 40 },  // class_code
          3: { cellWidth: 27 },  // time
          4: { cellWidth: 16 },  // hours
          5: { cellWidth: 32 },  // subject
          6: { cellWidth: 26 },  // teacher

        },
        margin: { top: 20, left: 10, right: 10 }
      });

      // Page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for(let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(128, 128, 128);
        doc.setFontSize(8);
        doc.text(
          `Page ${i} of ${pageCount}`, 
          doc.internal.pageSize.getWidth() / 2, 
          doc.internal.pageSize.getHeight() - 10, 
          { align: 'center' }
        );
      }

      doc.save(`${student.firstname}_${student.lastname}_schedule_${filenameSuffix}.pdf`);
      onClose();
    };

    headerImage.onerror = () => {
      console.error('Failed to load the header image.');
      Modal.error({
        title: t('Error'),
        content: t('Failed to load header image. Please try again.')
      });
    };
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      title={t('Download Class Schedule')}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('Cancel')}
        </Button>,
        <Button 
          key="download" 
          type="primary" 
          onClick={handleDownloadPDF}
          icon={<FontAwesomeIcon icon={faDownload} />}
        >
          {t('downloadPDF')}
        </Button>
      ]}
      width={400}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <div style={{ marginBottom: '16px' }}>
          <strong>{t('Select schedule type')}:</strong>
        </div>
        <Radio.Group 
          value={scheduleType} 
          onChange={(e) => setScheduleType(e.target.value)}
          style={{ width: '100%' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio value="upcoming">
              {t('Upcoming Classes')} ({upcomingSchedule.length})
            </Radio>
            <Radio value="past">
              {t('Past Classes')} ({pastSchedule.length})
            </Radio>
            <Radio value="all">
              {t('All Classes')} ({upcomingSchedule.length + pastSchedule.length})
            </Radio>
          </Space>
        </Radio.Group>
      </Space>
    </Modal>
  );
};

export default ScheduleDownloadModal;